import { EMPTY_ARRAY } from "@overmap-ai/common"
import {
	selectAssetTypesOfOrganization,
	selectIssueTypesOfOrganization,
	selectProjectById,
	selectProjectFiles,
} from "@overmap-ai/core"
import { getRouteApi, Outlet, useMatch } from "@tanstack/react-router"
import { Fragment, memo, useEffect, useMemo } from "react"

import { BOUNDS_PADDING } from "../mapbox/constants"
import { useCurrentMap } from "../mapbox/hooks"
import { AssetLayer, ProjectBoundsLayer, ProjectFileLayer } from "../mapbox/layers"
import { SelectedAssetLayer } from "../mapbox/layers/SelectedAssetLayer"
import { GeoImageMarkers, IssueMarkers } from "../mapbox/markers"
import { polygonToLngLatBounds } from "../mapbox/utils"
import { useAppSelector, useShallowEqaulSelector } from "../store"

const routeApi = getRouteApi("/_loggedIn/$organizationId/_map/_layout/$projectId/_projectMap")

export const ProjectMapLayout = memo(() => {
	const { organizationId, projectId } = routeApi.useParams()

	const project = useAppSelector(selectProjectById(projectId))

	if (!project) {
		throw new Error("No active project")
	}

	const map = useCurrentMap()

	useEffect(() => {
		if (!project.bounds || !map) return

		map.fitBounds(polygonToLngLatBounds(project.bounds), {
			animate: false,
			padding: BOUNDS_PADDING,
		})
	}, [project.bounds, map])

	const assetTypes = useShallowEqaulSelector(selectAssetTypesOfOrganization(organizationId)) ?? EMPTY_ARRAY
	const issueTypes = useShallowEqaulSelector(selectIssueTypesOfOrganization(organizationId)) ?? EMPTY_ARRAY
	const projectFiles = useAppSelector(selectProjectFiles)

	const sortedAssetTypes = useMemo(() => assetTypes.toSorted((a, b) => a.order - b.order), [assetTypes])
	const sortedProjectFiles = useMemo(() => projectFiles.toSorted((a, b) => a.z_index - b.z_index), [projectFiles])

	const match = useMatch({
		from: "/_loggedIn/$organizationId/_map/_layout/$projectId/_projectMap/",
		shouldThrow: false,
	})

	const activeIssueId = match?.search?.issue ?? null
	const activeAssetId = match?.search?.asset ?? null

	return (
		<Fragment>
			{sortedProjectFiles.map((projectFile) => (
				<ProjectFileLayer key={projectFile.uuid} projectFileId={projectFile.uuid} />
			))}

			<ProjectBoundsLayer project={project} />

			<SelectedAssetLayer assetId={activeAssetId} />

			{sortedAssetTypes.map((assetType, index) => (
				<AssetLayer
					assetTypeId={assetType.uuid}
					key={assetType.uuid}
					beforeAssetTypeId={sortedAssetTypes[index - 1]?.uuid}
				/>
			))}

			{issueTypes.map((issueType) => (
				<IssueMarkers issueTypeId={issueType.uuid} activeIssueId={activeIssueId} key={issueType.uuid} />
			))}

			<GeoImageMarkers projectId={project.uuid} />

			<Outlet />
		</Fragment>
	)
})
ProjectMapLayout.displayName = "ProjectMapLayout"
